import React from "react";
import Navigation from "../Navigation/navigation";
import Footer from "../Footer/Footer";
import PandaBuySignUpLink from "../PandaBuySignUpLink/PandaBuySignUpLink";
import { Helmet } from "react-helmet";
import { AgentName, PageTitle } from "../../config/config";


const Faq = () => {
  return (
    <div className="bg-white">
      <Helmet>
        <title>
          {PageTitle}: Step-by-Step, Quality Check, and Exclusive Discounts
        </title>
        <meta
          name="description"
          content="Discover the best way to buy from Superbuy with our comprehensive step-by-step guide. Explore a vast selection of over 7000 unique products on https://www.superbuyspreadsheet.net/ with essential details, QC links, and enjoy exclusive discounts. Find Superbuy shoes, use discount codes, calculate shipping costs, and more."
        />
        <meta
          name="keywords"
          content="Superbuy, Superbuy shoes, Superbuy discount code, Superbuy coupon, buy from Superbuy, Superbuy shipping, Superbuy spreadsheet, Superbuy clothing, step-by-step buying, quality check, shipping process, Superbuy shipping estimator"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.superbuyspreadsheet.net/guide" />
        <link
          rel="sitemap"
          type="application/xml"
          title="Sitemap"
          href="https://www.superbuyspreadsheet.net/sitemap.xml"
        />
        <script type="application/ld+json" src="/schema.org.json"></script>
      </Helmet>

      <PandaBuySignUpLink />
      <Navigation />

      <div className="container mx-auto px-4 py-12">
        {/* Section 1: Signup Offer */}
        <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-12">
          <h1 className="text-xl lg:text-3xl font-bold mb-4">Signup Offer</h1>
          <p className="text-md lg:text-lg mb-4">
            Sign up now             <a
              href="https://www.superbuy.com/en/page/login/?partnercode=wUr6VT&type=register "
              target="_blank"
              rel="noreferrer"
              className="text-blue-700 hover:underline"
            >
              Claim Your $10 Coupon
            </a> and enjoy a fantastic $10 discount coupon on your first order with {PageTitle}!
          </p>
          <p className="mb-4 text-md lg:text-lg">
            Thank you for signing up using my exclusive link! Your support means a lot. Happy shopping!
          </p>
        </div>


        {/* Step-by-Step Guide: How to Buy from Superbuy */}
        <article className="my-12">
          <h1 className="text-3xl font-bold text-center mb-8 text-gray-900">How to Buy from {AgentName}: A Step-by-Step Guide</h1>

          {/* Step 1: Finding the Items */}
          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg lg:text-xl font-bold">01. Finding the Items</h2>
            <p className="text-sm lg:text-lg">
              Browse China's online e-commerce platforms, such as Taobao, Tmall, or JD.com. You can also discover products via social media, such as Reddit, Instagram, or TikTok. Copy the product link, shop's link, or order number and paste it into the {AgentName} search bar.
              <br />
              Alternatively, you can browse the {AgentName} spreadsheet for direct links to {AgentName} products.
            </p>
          </div>

          {/* Step 2: Submit Your Order */}
          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg lg:text-xl font-bold">02. Submit Your Order</h2>
            <p className="text-sm lg:text-lg">
              When submitting your order, you have the option to select the color, size, and quantity of your desired items. You can even add special remarks. Simply click “Buy Now” or “Add to Cart”.
              <br />
              {AgentName} prioritizes your shopping convenience. To ensure your satisfaction, they provide Quality Check pictures of previously sold and shipped items. Before placing your order, you'll also have access to important details like sales data, average shipping time, weight, volume, and net weight/volume. For VIP customers with 300+ points, you can even view in-warehouse photos before finalizing your purchase!
            </p>
          </div>

          {/* Step 3: Confirm Order */}
          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg lg:text-xl font-bold">03. Confirm Order</h2>
            <p className="text-sm lg:text-lg">
              Choose the destination country. Once your purchase is made, your items will be sent to the {AgentName} warehouse in China. After the order is received and stored in the warehouse, you can access it through the Personal Center - Warehouse page.
              <br />
              From there, you can submit an international parcel request and provide your shipping details. If you need more comprehensive pictures beyond the Quality Check photos, you can opt for Extra Photos before finalizing your order.
            </p>
          </div>

          {/* Step 4: Payment Process */}
          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg lg:text-xl font-bold">04. Payment Process</h2>
            <p className="text-sm lg:text-lg">
              Choose your country and preferred payment method. You will need to cover the cost of the product and the domestic shipping fee (charged by the seller). Note that international shipping fees are not included.
              <br />
              Accepted Payment Methods: PayPal, Credit Cards, Alipay Plus, Stripe, UnionPay, Alipay, Applepay, and more.
            </p>
          </div>

          {/* Step 5: Superbuy Purchase */}
          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg lg:text-xl font-bold">05. {AgentName} Purchase</h2>
            <p className="text-sm lg:text-lg">
              After confirming your order, {AgentName} team initiates processing within 6 working hours. They will contact the seller to acquire your items, awaiting shipment to their facility, which typically takes about 3 to 5 working days.
              <br />
              You will be notified if any issues arise during the process, and {AgentName} will promptly respond to your queries within 24 hours via the order enquiry section.
            </p>
          </div>

          {/* Step 6: Quality Assurance and Warehousing */}
          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg lg:text-xl font-bold">06. {AgentName} Quality Assurance and Warehousing</h2>
            <p className="text-sm lg:text-lg">
            {AgentName} ensures rigorous quality checks for your items. These are available for review in the Personal Center - Warehouse section. After accepting the product, {AgentName} will securely store it.
              <br />
              If you're dissatisfied, {AgentName} agents will assist in negotiating returns or exchanges with the seller. Items are stored for up to 90 days at no charge, allowing you to consolidate purchases and ship them together, saving on shipping costs.
            </p>
          </div>

          {/* Step 7: Submit for Delivery */}
          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg lg:text-xl font-bold">07. Submit for Delivery</h2>
            <p className="text-sm lg:text-lg">
              Once your items are stored, visit the 'Warehouse' section. Select the item and click "submit" to organize international delivery. Enter your overseas address, choose your shipping method, and finalize the international shipping payment.
            </p>
          </div>

          {/* Step 8: Pay the International Shipping Cost */}
          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg lg:text-xl font-bold">08. Pay the International Shipping Cost</h2>
            <p className="text-sm lg:text-lg">
              Choose your preferred payment method to cover the international shipping cost. The shipping fee depends on factors such as weight, shipping method, and destination.
              <br />
              For assistance in estimating shipping fees, please visit{" "}
              <a
                href="https://www.superbuy.com/en/page/query/freight/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 hover:underline"
              >
                {AgentName} Shipping Estimator
              </a>.
            </p>
          </div>

          {/* Step 9: Delivery and Rewards */}
          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg lg:text-xl font-bold">09. Delivery and Rewards</h2>
            <p className="text-sm lg:text-lg">
              Once your delivery order is submitted, {AgentName} carefully packages and ships your items. Track your parcel's journey through the "Parcel" page using the provided tracking number.
              <br />
              Upon receiving your parcel, feel free to share your experience by reviewing the product on {AgentName} and apply for rewards.
            </p>
          </div>

          <div className="bg-white text-gray-900 p-6 rounded-lg shadow-md mb-12">
            <h2 className="text-lg lg:text-xl font-bold mb-4">Summary</h2>
            <p className="text-sm lg:text-lg">
              By following this comprehensive guide and utilizing {AgentName} spreadsheet, you'll confidently navigate the process of buying items from {AgentName}. Save your favorite items to the watchlist for quick access, easy comparison, and price checking. Enjoy a seamless shopping experience with over 5000 unique products at your fingertips. Happy shopping!
            </p>
          </div>
        </article>

        {/* Footer Section */}
        <Footer />
      </div>
    </div>
  );
};

export default Faq;
